import {
  IconBuilding,
  IconFlag,
  IconGitCommit,
  IconActivity,
  IconPlugConnected,
  IconFolders,
  IconUsersGroup,
  IconTimeline,
  IconRefresh,
  IconDatabase,
  IconServer,
  IconCloudDownload,
  IconCloudUpload,
  IconChecklist,
  IconBulb,
  IconRobot,
  IconActivityHeartbeat,
  IconGitBranch,
  IconUserSearch,
  IconArrowRight,
  IconCalculator,
  IconRotateClockwise,
  IconShieldCheck,
  IconPlugOff,
  IconGitCompare,
  IconReportAnalytics,
} from '@tabler/icons-react';
import { SiJira } from 'react-icons/si';

export type NavigationCategory = 'management' | 'monitoring' | 'infrastructure' | 'onprem' | 'insights' | 'agent' | 'health' | 'console-audit' | 'backfill' | 'data-hygiene' | 'maker-checker';

export interface NavigationItem {
  text: string;
  path: string;
  icon: React.ComponentType<any>;
  category: NavigationCategory;
  pageTitle: string; // The title shown on the page itself
}

export const navigationItems: NavigationItem[] = [
  // Management Category
  {
    text: 'Organizations',
    path: '/organizations',
    icon: IconBuilding,
    category: 'management',
    pageTitle: 'Organizations',
  },
  {
    text: 'Feature Management',
    path: '/feature-management',
    icon: IconFlag,
    category: 'management',
    pageTitle: 'Feature Management',
  },
  // Data Hygiene Category
  {
    text: 'Data Hygiene',
    path: '/data-hygiene',
    icon: IconShieldCheck,
    category: 'data-hygiene',
    pageTitle: 'Data Hygiene',
  },
  {
    text: 'Orphaned (No-Org) Data',
    path: '/data-hygiene/orphaned-data',
    icon: IconPlugOff,
    category: 'data-hygiene',
    pageTitle: 'Orphaned (No-Org) Data',
  },
  {
    text: 'Calculation Hygiene',
    path: '/data-hygiene/calculation',
    icon: IconGitCommit,
    category: 'data-hygiene',
    pageTitle: 'Calculation Hygiene',
  },
  // Monitoring Category
  {
    text: 'Coding Metrics',
    path: '/coding-metrics',
    icon: IconGitCommit,
    category: 'monitoring',
    pageTitle: 'Coding Metrics',
  },
  {
    text: 'Webhook Insights',
    path: '/webhooks',
    icon: IconActivity,
    category: 'monitoring',
    pageTitle: 'Webhook Insights',
  },
  {
    text: 'Integration Stats',
    path: '/integration-stats',
    icon: IconPlugConnected,
    category: 'monitoring',
    pageTitle: 'Integration Stats',
  },
  {
    text: 'Incremental Sync',
    path: '/incremental-sync',
    icon: IconTimeline,
    category: 'monitoring',
    pageTitle: 'Incremental Sync',
  },
  {
    text: 'Jira Integration Status',
    path: '/jira-sync',
    icon: IconChecklist,
    category: 'monitoring',
    pageTitle: 'Jira Integration Status',
  },
  {
    text: 'Agent Monitoring',
    path: '/agent-monitoring',
    icon: IconActivityHeartbeat,
    category: 'agent',
    pageTitle: 'Agent Monitoring',
  },
  {
    text: 'Agent Sync Monitor',
    path: '/agent-sync',
    icon: IconRobot,
    category: 'agent',
    pageTitle: 'Agent Sync Monitor',
  },
  {
    text: 'Agent Migration',
    path: '/agent-migration',
    icon: IconArrowRight,
    category: 'agent',
    pageTitle: 'Agent Migration',
  },
  {
    text: 'Active Syncs',
    path: '/active-syncs',
    icon: IconRefresh,
    category: 'monitoring',
    pageTitle: 'Active Syncs',
  },
  {
    text: 'Repository Insights',
    path: '/repo-insights',
    icon: IconFolders,
    category: 'monitoring',
    pageTitle: 'Repository Insights',
  },
  {
    text: 'Author Insights',
    path: '/author-insights',
    icon: IconUsersGroup,
    category: 'monitoring',
    pageTitle: 'Author Insights',
  },
  {
    text: 'Missing Data Insights',
    path: '/missing-data-insights',
    icon: IconDatabase,
    category: 'monitoring',
    pageTitle: 'Missing Data Insights',
  },
  {
    text: 'Data Ingestion',
    path: '/data-ingestion',
    icon: IconCloudDownload,
    category: 'monitoring',
    pageTitle: 'Data Ingestion',
  },
  // Infrastructure Category
  {
    text: 'ECS Deployments',
    path: '/ecs-deployments',
    icon: IconServer,
    category: 'infrastructure',
    pageTitle: 'ECS Deployments',
  },
  {
    text: 'Migration',
    path: '/migration',
    icon: IconCloudUpload,
    category: 'infrastructure',
    pageTitle: 'Migration',
  },
  // Backfill Category
  {
    text: 'Hivel Score Sync',
    path: '/hivel-score-sync',
    icon: IconCalculator,
    category: 'backfill',
    pageTitle: 'Hivel Score Sync',
  },
  // Maker Checker Category
  {
    text: 'Maker Checker',
    path: '/maker-checker',
    icon: IconGitCompare,
    category: 'maker-checker',
    pageTitle: 'Maker Checker',
  },
  {
    text: 'Metric Checker',
    path: '/metric-checker',
    icon: IconReportAnalytics,
    category: 'maker-checker',
    pageTitle: 'Metric Checker',
  },
  {
    text: 'Agent Sync Status',
    path: '/agent-repull',
    icon: IconRotateClockwise,
    category: 'agent',
    pageTitle: 'Hivel Agent Sync Status',
  },
  // Insights Category
  {
    text: 'Insights',
    path: '/insights',
    icon: IconBulb,
    category: 'insights',
    pageTitle: 'Insights',
  },
  // Health Dashboard Category
  {
    text: 'Jira Monitoring',
    path: '/jira-monitoring',
    icon: SiJira,
    category: 'health',
    pageTitle: 'Jira Monitoring',
  },
  {
    text: 'Git Monitoring',
    path: '/git-monitoring',
    icon: IconGitBranch,
    category: 'health',
    pageTitle: 'Git Monitoring',
  },
  // Console Audit Category
  {
    text: 'Login Audit',
    path: '/login-audit',
    icon: IconUserSearch,
    category: 'console-audit',
    pageTitle: 'Login Audit',
  },
  // On-Prem Category
  {
    text: 'Organizations',
    path: '/on-prem/organizations',
    icon: IconBuilding,
    category: 'onprem',
    pageTitle: 'On-Prem Organizations',
  },
  {
    text: 'Releases',
    path: '/on-prem/releases',
    icon: IconCloudUpload,
    category: 'onprem',
    pageTitle: 'On-Prem Releases',
  },
  {
    text: 'Release History',
    path: '/on-prem/history',
    icon: IconTimeline,
    category: 'onprem',
    pageTitle: 'On-Prem Release History',
  },
];

// Helper functions to get items by category
export const getNavigationItemsByCategory = (category: NavigationCategory): NavigationItem[] => {
  return navigationItems.filter((item) => item.category === category);
};

export const getNavigationItemByPath = (path: string): NavigationItem | undefined => {
  return navigationItems.find((item) => item.path === path);
};

// Category labels
export const categoryLabels: Record<NavigationCategory, string> = {
  management: 'Management',
  monitoring: 'Monitoring',
  infrastructure: 'Infrastructure',
  onprem: 'On-Prem',
  insights: 'Insights',
  agent: 'Hivel Agent',
  health: 'Health Dashboard',
  'console-audit': 'Console Audit',
  backfill: 'Backfill',
  'data-hygiene': 'Data Hygiene',
  'maker-checker': 'Maker Checker',
};

